export type SocialPlatform = 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'github';

interface PlatformMatcher {
  platform: SocialPlatform;
  hosts: string[];
  extract: (path: string) => string | undefined;
}

const FACEBOOK_RESERVED = new Set([
  'pages',
  'events',
  'groups',
  'marketplace',
  'watch',
  'gaming',
  'help',
  'login',
  'home.php',
]);

const MATCHERS: PlatformMatcher[] = [
  {
    platform: 'linkedin',
    hosts: ['linkedin.com', 'www.linkedin.com'],
    extract: (path) => {
      const m = /^\/in\/([^/]+)/i.exec(path);
      return m?.[1];
    },
  },
  {
    platform: 'twitter',
    hosts: ['twitter.com', 'www.twitter.com', 'x.com', 'www.x.com'],
    extract: (path) => {
      const m = /^\/([^/]+)/.exec(path);
      const slug = m?.[1];
      if (!slug || slug === 'home' || slug === 'i') return undefined;
      return slug;
    },
  },
  {
    platform: 'facebook',
    hosts: ['facebook.com', 'www.facebook.com', 'm.facebook.com'],
    extract: (path) => {
      const m = /^\/([^/]+)/.exec(path);
      const slug = m?.[1];
      if (!slug || FACEBOOK_RESERVED.has(slug.toLowerCase())) return undefined;
      return slug;
    },
  },
  {
    platform: 'instagram',
    hosts: ['instagram.com', 'www.instagram.com'],
    extract: (path) => {
      const m = /^\/([^/]+)/.exec(path);
      const slug = m?.[1];
      if (!slug || slug === 'p' || slug === 'reel' || slug === 'explore') return undefined;
      return slug;
    },
  },
  {
    platform: 'github',
    hosts: ['github.com', 'www.github.com'],
    extract: (path) => {
      const m = /^\/([^/]+)/.exec(path);
      const slug = m?.[1];
      if (!slug || slug === 'orgs' || slug === 'topics' || slug === 'features') return undefined;
      return slug;
    },
  },
];

export interface ParsedSocialUrl {
  platform: SocialPlatform;
  username: string;
}

export function parseSocialUrl(input: string): ParsedSocialUrl | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return undefined;
  }
  const host = url.hostname.toLowerCase();
  for (const m of MATCHERS) {
    if (!m.hosts.includes(host)) continue;
    const username = m.extract(url.pathname);
    if (!username) return undefined;
    return { platform: m.platform, username };
  }
  return undefined;
}
